/**
 * Redux slice for records using consistent API response format
 * This will replace the legacy recordsSlice.js
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import recordsService from '../services/recordsService';
import { Record, ApiResponse, PaginationInfo, RecordsQueryFilters, QueryResult, RecordsApiResponse } from '../types/apiTypes';
import { handleApiError } from '../utils/consistentApiParser';

// Async thunks
export const queryRecords = createAsyncThunk(
  'records/queryRecords',
  async (filters: RecordsQueryFilters, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'test') {
        console.log('🚀 recordsSlice: Querying records with filters:', filters);
      }

      const response = await recordsService.queryRecords(filters);

      if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'test') {
        console.log('recordsSlice: Received response from service:', response);
      }

      return response;
    } catch (error) {
      console.error('❌ recordsSlice: Error querying records:', error);
      const errorMessage = handleApiError(error, 'consistentRecordsSlice', 'queryRecords');
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRecordById = createAsyncThunk(
  'records/getRecordById',
  async (recordId: string, { rejectWithValue }) => {
    try {
      const response = await recordsService.getRecordById(recordId);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentRecordsSlice', 'getRecordById');
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadRecords = createAsyncThunk(
  'records/uploadRecords',
  async (params: { file: File; channelId?: string }, { rejectWithValue }) => {
    try {
      const response = await recordsService.uploadRecords(params.file, params.channelId);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentRecordsSlice', 'uploadRecords');
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRecordsWithPresignedUrls = createAsyncThunk(
  'records/getRecordsWithPresignedUrls',
  async (filters: RecordsQueryFilters, { rejectWithValue }) => {
    try {
      const response = await recordsService.getRecordsWithPresignedUrls(filters);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentRecordsSlice', 'getRecordsWithPresignedUrls');
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteRecord = createAsyncThunk(
  'records/deleteRecord',
  async (recordId: string, { rejectWithValue }) => {
    try {
      const response = await recordsService.deleteRecord(recordId);
      return { recordId, response };
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentRecordsSlice', 'deleteRecord');
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRecordsTimeline = createAsyncThunk(
  'records/getRecordsTimeline',
  async (params: { filters: RecordsQueryFilters; interval: 'hour' | 'day' | 'week' | 'month' }, { rejectWithValue }) => {
    try {
      const response = await recordsService.getRecordsTimeline(params.filters, params.interval);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, 'consistentRecordsSlice', 'getRecordsTimeline');
      return rejectWithValue(errorMessage);
    }
  }
);

// State interface
interface ConsistentRecordsState {
  records: Record[];
  currentRecord: Record | null;
  loading: boolean;
  error: string | null;
  success: boolean;
  count: number;
  limitApplied: number | null;
  pagination: PaginationInfo | null;
  message: string | null;
  filters: RecordsQueryFilters;
  timeline: Array<{ timestamp: string; count: number }>;
  timelineLoading: boolean;
  uploadProgress: number | null;
}

const initialState: ConsistentRecordsState = {
  records: [],
  currentRecord: null,
  loading: false,
  error: null,
  success: false,
  count: 0,
  limitApplied: null,
  pagination: null,
  message: null,
  filters: {
    start_time: '',
    end_time: '',
    'source.source_id': '',
    'source.name': '',
    'source.supplier_id': '',
    'source.platform_id': '',
    'source.sensor_id': '',
    leader_id: '',
    limit: 500  // Default to 500 records
  },
  timeline: [],
  timelineLoading: false,
  uploadProgress: null,
};

const recordsSlice = createSlice({
  name: 'records',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setCurrentRecord: (state, action: PayloadAction<Record | null>) => {
      state.currentRecord = action.payload;
    },
    clearCurrentRecord: (state) => {
      state.currentRecord = null;
    },
    setFilters: (state, action: PayloadAction<Partial<RecordsQueryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        start_time: '',
        end_time: '',
        'source.source_id': '',
        'source.name': '',
        'source.supplier_id': '',
        'source.platform_id': '',
        'source.sensor_id': '',
        leader_id: '',
        limit: 500  // Reset to default 500 records
      };
    },
    clearRecords: (state) => {
      state.records = [];
      state.count = 0;
      state.limitApplied = null;
      state.pagination = null;
    },
    setUploadProgress: (state, action: PayloadAction<number | null>) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Query Records
      .addCase(queryRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(queryRecords.fulfilled, (state, action) => {
        const response = action.payload as RecordsApiResponse<Record>;
        state.loading = false;

        if (response.data) {
          state.records = response.data;
          state.count = response.count || response.data.length;
          state.limitApplied = response.limit_applied || null;
        }

        state.pagination = response.pagination || null;
        state.message = response.message || null;
        state.success = true;
      })
      .addCase(queryRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Get Record By ID
      .addCase(getRecordById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRecordById.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<Record>;
        state.loading = false;
        state.currentRecord = response.data || null;
        state.message = response.message || null;
      })
      .addCase(getRecordById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Upload Records
      .addCase(uploadRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.uploadProgress = 0;
      })
      .addCase(uploadRecords.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<{ uploaded_count: number; errors?: any[] }>;
        state.loading = false;
        state.success = true;
        state.message = response.message || `Uploaded ${response.data?.uploaded_count || 0} records`;
        state.uploadProgress = null;
      })
      .addCase(uploadRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
        state.uploadProgress = null;
      })

      // Get Records with Presigned URLs
      .addCase(getRecordsWithPresignedUrls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRecordsWithPresignedUrls.fulfilled, (state, action) => {
        const response = action.payload as RecordsApiResponse<Record>;
        state.loading = false;

        if (response.data) {
          state.records = response.data;
          state.count = response.count || response.data.length;
        }

        state.pagination = response.pagination || null;
        state.message = response.message || null;
      })
      .addCase(getRecordsWithPresignedUrls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete Record
      .addCase(deleteRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteRecord.fulfilled, (state, action) => {
        const { recordId } = action.payload;
        state.loading = false;
        state.records = state.records.filter(record => record.record_id !== recordId);
        state.count = Math.max(0, state.count - 1);
        state.success = true;
        state.message = 'Record deleted successfully';
      })
      .addCase(deleteRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Get Records Timeline
      .addCase(getRecordsTimeline.pending, (state) => {
        state.timelineLoading = true;
        state.error = null;
      })
      .addCase(getRecordsTimeline.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<Array<{ timestamp: string; count: number }>>;
        state.timelineLoading = false;
        state.timeline = response.data || [];
      })
      .addCase(getRecordsTimeline.rejected, (state, action) => {
        state.timelineLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearMessage,
  setCurrentRecord,
  clearCurrentRecord,
  setFilters,
  clearFilters,
  clearRecords,
  setUploadProgress,
} = recordsSlice.actions;

export default recordsSlice.reducer;
